import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Database,
  Activity,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Eye,
  Settings,
  Users,
  Clock,
  TrendingUp,
  Server,
  Code,
  Wrench,
  Play,
  Sparkles,
  Brain
} from "lucide-react";
import { SqlHealthProLogo } from "@/components/logos";
import { useLocation, Link } from "wouter";

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [, navigate] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: Implement actual signup logic
    console.log("Sign up with email:", email, "AI Enabled:", aiEnabled);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert(`Thank you for your interest! We'll be in touch soon.\nAI Features: ${aiEnabled ? 'Enabled' : 'Disabled'}`);
      setEmail("");
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-shiftleft-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SqlHealthProLogo size={32} />
              <span className="text-2xl font-bold text-gray-900">ShiftLeft Digital</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <Link href="/about" className="text-gray-600 hover:text-gray-900">About</Link>
              <Button variant="outline" onClick={() => navigate("/login")}>Sign In</Button>
              <Button onClick={() => document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' })}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Badge className="bg-shiftleft-100 text-shiftleft-800 border-shiftleft-200">
              Comprehensive SQL Server Management
            </Badge>
            <h1 className="text-5xl font-bold text-gray-900 leading-tight">
              Monitor your databases for{" "}
              <span className="text-shiftleft-600">reliability</span> and{" "}
              <span className="text-shiftleft-600">performance</span> with AI
            </h1>
            <p className="text-xl text-gray-600">
              Advanced real-time monitoring combined with insightful performance analysis powered by AI.
              A complete platform for SQL Server operations, optimization, and management.
            </p>

            {/* AI Preference Toggle */}
            <Card className="border-2 border-shiftleft-200 bg-shiftleft-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${aiEnabled ? 'bg-shiftleft-600' : 'bg-gray-400'}`}>
                      {aiEnabled ? <Brain className="w-5 h-5 text-white" /> : <Database className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <Label htmlFor="ai-toggle" className="text-base font-semibold text-gray-900 cursor-pointer">
                        {aiEnabled ? 'AI-Powered Features' : 'Traditional Monitoring'}
                      </Label>
                      <p className="text-sm text-gray-600">
                        {aiEnabled
                          ? 'Get intelligent insights and recommendations'
                          : 'Standard monitoring without AI assistance'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="ai-toggle"
                    checked={aiEnabled}
                    onCheckedChange={setAiEnabled}
                    className="data-[state=checked]:bg-shiftleft-600"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Email Signup Form */}
            <form onSubmit={handleSignUp} className="flex gap-3" id="signup-form">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 h-12 text-lg"
              />
              <Button type="submit" size="lg" disabled={isSubmitting} className="h-12 px-8">
                {isSubmitting ? "Joining..." : "Join Early Access"}
              </Button>
            </form>

            {/* Demo Access */}


            <p className="text-sm text-gray-500">
              Join our early access program to get early access to the service, special offers and discounts.
            </p>
          </div>

          {/* Hero Image/Illustration */}
          <div className="relative">
            <Card className="bg-shiftleft-600 text-white">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Activity className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-sm opacity-90">Real-time Monitoring</div>
                      <div className="text-2xl font-bold">300+ DMVs</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-sm opacity-90">Performance Analysis</div>
                      <div className="text-2xl font-bold">AI-Powered</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-sm opacity-90">Security & Compliance</div>
                      <div className="text-2xl font-bold">Enterprise Grade</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything you need for SQL Server excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools for monitoring, optimization, and management of your SQL Server environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Cards */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-shiftleft-100 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-shiftleft-600" />
                </div>
                <CardTitle>Observability Dashboard</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Monitor 300+ Dynamic Management Views, Query Store, and Extended Events in real-time with comprehensive categorization.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>OStress Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Execute stress tests with OStress, monitor CPU, memory, I/O, and tempdb performance during load testing.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Code className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>Code Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Static analysis, code review, stored procedure optimization, and security scanning for your SQL code.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>Operations Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Database operations, security management, backup/restore, and maintenance tasks all in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Performance Diagnostics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Comprehensive diagnostics suite with AI-powered insights for query optimization and bottleneck identification.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle>Security & Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  User management, role-based access control, audit trails, and compliance reporting built-in.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why ShiftLeft Digital?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Complete Visibility</h3>
                    <p className="text-gray-600">Access all 300+ SQL Server DMVs organized by category with real-time data viewing</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">AI-Powered Insights</h3>
                    <p className="text-gray-600">Get intelligent recommendations and automated performance analysis</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Comprehensive Testing</h3>
                    <p className="text-gray-600">Built-in OStress integration for realistic load testing and stress analysis</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">All-in-One Platform</h3>
                    <p className="text-gray-600">Monitor, optimize, manage, and secure your SQL Server environments from one dashboard</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Enterprise Ready</h3>
                    <p className="text-gray-600">Multi-tenant architecture with role-based access control and audit trails</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-shiftleft-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-8 h-8 text-shiftleft-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">99.9%</div>
                      <div className="text-sm text-gray-600">Uptime SLA</div>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    Enterprise-grade reliability with 24/7 monitoring and support
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <Clock className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">&lt;5min</div>
                      <div className="text-sm text-gray-600">Setup Time</div>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    Get started in minutes with our simple onboarding process
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">1000+</div>
                      <div className="text-sm text-gray-600">Databases Monitored</div>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    Trusted by organizations managing critical SQL Server workloads
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-shiftleft-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to optimize your SQL Server?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join our early access program and be among the first to experience the future of SQL Server management
          </p>

          <form onSubmit={handleSignUp} className="flex gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 h-12 text-gray-900"
            />
            <Button type="submit" size="lg" variant="secondary" disabled={isSubmitting} className="h-12 px-8">
              {isSubmitting ? "Joining..." : "Get Started"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-6 h-6 text-shiftleft-500" />
                <span className="text-xl font-bold text-white">ShiftLeft Digital</span>
              </div>
              <p className="text-sm">
                Comprehensive SQL Server management and optimization platform
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link href="/about" className="hover:text-white">About</Link></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} CloudBaud, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}